import { useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from '../themeToggle';
import { useDispatch, useSelector } from 'react-redux';
import type { CustomDispatch, RootState } from '../../state/store';
import { navigationOperations } from '../../state/features/navigation';
import styled from 'styled-components';
import { useFilterNavigate } from '../../helpers/hooks/useFilterNavigate';
import Logo from './logo';
import { ENTITY_LOCK_TYPE } from '../../types/visibility';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavItemIcon } from './navItemIcon';
import { SignalsNavItem } from './signalsNavItem';
import { InsightsNavDropdown } from './insightsNavDropdown';
import { buildStandaloneNavItems } from './standaloneNavItems';
import {
  Navbar as BSNavbar,
  Nav as BSNav,
  Offcanvas,
  OverlayTrigger,
  Popover
} from 'react-bootstrap';
import { PiDotsThreeCircleFill } from 'react-icons/pi';
import { IoClose } from 'react-icons/io5';
import { authOperations } from '../../state/features/auth';
import askVantageIcon from '../../images/sapaad-vantage-button-icon.svg';
import AppDrawer from '../appDrawer';
import { ASK_VANTAGE_HOME_EVENT } from '../../features/askVantageV2/theme/constants';

// Define Dashboard type based on server response structure
interface Dashboard {
  label: string;
  identifier: string;
  api_path: string;
  visibility: ENTITY_LOCK_TYPE;
}

interface NavbarProps {
  fixed: 'top' | null;
  top?: number;
}

const Navbar: React.FC<NavbarProps> = ({ fixed, top = 0 }) => {
  const companyDetails = useSelector((state: RootState) => state.company?.company_details as any);
  const askVantageV2Enabled = companyDetails?.ask_vantage_v2_enabled;
  const askVantageV1Enabled = companyDetails?.ask_vantage_enabled;
  const askVantageTarget = askVantageV2Enabled ? '/ask' : '/ask-vantage';
  // `signals_status` is sent by `/api/v1/me`. Default to disabled so Signals
  // stays hidden until the server explicitly exposes it.
  const signalsStatus: 'enabled' | 'coming_soon' | 'disabled' =
    companyDetails?.signals_status === 'enabled' || companyDetails?.signals_status === 'coming_soon'
      ? companyDetails.signals_status
      : 'disabled';
  const visibleSignalsStatus = signalsStatus === 'disabled' ? null : signalsStatus;
  const promotionEngineEnabled = companyDetails?.promotion_engine_status === 'enabled';
  const revenueLeakageEnabled = companyDetails?.revenue_leakage_status === 'enabled';
  const standaloneNavItems = useMemo(
    () =>
      buildStandaloneNavItems({
        promotionEngineEnabled,
        revenueLeakageEnabled
      }),
    [promotionEngineEnabled, revenueLeakageEnabled]
  );
  const companyName = companyDetails?.company_name ?? '';
  const { dashboards } = useSelector((state: RootState) => state.dashboard);
  const { enableCompanySwitch } = useSelector((state: RootState) => state.auth);
  const navigateTo = useFilterNavigate();
  const dispatch: CustomDispatch = useDispatch();
  const { toggleCompanySwitcher } = authOperations;
  const [enableOverflowPopover, setEnableOverflowPopover] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  const companyNameRef = useRef<HTMLSpanElement>(null);

  const filterUnauthorizedDashboards = (data: Dashboard[]) => {
    return data.filter(
      (d) => ![ENTITY_LOCK_TYPE.INVISIBLE, ENTITY_LOCK_TYPE.UNAUTHORIZED].includes(d.visibility)
    );
  };

  const [, path] = location.pathname.split('/');

  const accessibleDashboards = useMemo(
    () => filterUnauthorizedDashboards(dashboards as any[]),
    [dashboards]
  );

  const handleNavigation = (route: string) => {
    dispatch(navigationOperations.setNavigation(route));
    navigateTo(route);
    setShowOffcanvas(false); // Close offcanvas after navigation
  };

  const handleAskVantageClick = () => {
    if (location.pathname === askVantageTarget) {
      window.dispatchEvent(new CustomEvent(ASK_VANTAGE_HOME_EVENT));
      return;
    }
    navigate(askVantageTarget);
  };

  const expand = 'lg';

  // Logo-click destination: Signals takes priority for enabled merchants;
  // everyone else falls back to the root dashboard.
  const logoDestination = signalsStatus === 'enabled' ? '/signals' : '/';

  const handleBrandingClick = () => {
    if (enableCompanySwitch) {
      toggleCompanySwitcher(true);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent, route: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigation(route);
    }
  };

  // Close offcanvas on Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showOffcanvas) {
        setShowOffcanvas(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [showOffcanvas]);

  useEffect(() => {
    if (companyName && companyNameRef.current) {
      const isOverflow = companyNameRef.current.scrollWidth > companyNameRef.current.clientWidth;
      setEnableOverflowPopover(isOverflow);
    }
  }, [companyName]);

  const renderTooltip = (props: any) => {
    return (
      <Popover {...props} className="company-name-popover">
        <Popover.Body>
          <h4>{companyName}</h4>
        </Popover.Body>
      </Popover>
    );
  };

  const renderCompanyName = ({
    companyName,
    enableCompanySwitch
  }: {
    companyName: string;
    enableCompanySwitch: boolean;
  }) => {
    return (
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        trigger={enableOverflowPopover ? ['hover', 'focus'] : []}
        overlay={renderTooltip}
      >
        <span>
          <span className="company-name" ref={companyNameRef}>
            {companyName}
          </span>
          {enableCompanySwitch ? (
            <PiDotsThreeCircleFill
              style={{ marginLeft: '10px', fontSize: '23px' }}
              color="#b1b1b1"
            />
          ) : null}
        </span>
      </OverlayTrigger>
    );
  };

  return (
    <>
      <StyledBSNavbar bg="dark" variant="dark" expand="lg" fixed={fixed} top={top}>
        <div className="container-fluid">
          {/* Left section with logo and company name */}
          <div className="navbar-left-section">
            <div
              className="company-logo d-none d-lg-flex"
              onClick={() => { if (logoDestination) navigate(logoDestination); }}
              style={{ cursor: logoDestination ? 'pointer' : 'default' }}
              role={logoDestination ? 'button' : undefined}
              tabIndex={logoDestination ? 0 : undefined}
              aria-label={`Go to ${logoDestination === '/signals' ? 'Signals' : 'dashboard'}`}
              onKeyDown={(e) => { if (e.key === 'Enter' && logoDestination) navigate(logoDestination); }}
            >
              <Logo />
            </div>
            <StyledBSNavbar.Brand className="d-flex mx-auto mx-lg-0">
              <div
                className={`company-name-wrapper ${enableCompanySwitch ? 'enable-selection' : ''}`}
                onClick={handleBrandingClick}
              >
                {renderCompanyName({ companyName, enableCompanySwitch })}
              </div>
            </StyledBSNavbar.Brand>
          </div>

          {/* Center section with navigation - visible only on desktop */}
          <BSNav className="main-nav me-auto d-none d-lg-flex">
            {visibleSignalsStatus && (
              <SignalsNavItem
                variant="desktop"
                status={visibleSignalsStatus}
                isSelected={location.pathname === '/signals'}
                onClick={() => navigate('/signals')}
              />
            )}
            {accessibleDashboards.map((d: Dashboard, i: number) => {
              const isSelected = d.api_path === path;
              return (
                <BSNav.Link key={d.api_path} onClick={() => handleNavigation(`/${d.api_path}`)}>
                  <span className={`dbrd-nav-item ${isSelected ? 'selected' : ''}`}>
                    <span className="icon">
                      <NavItemIcon name={d.identifier} isSelected={isSelected} />
                    </span>
                    <span className="dbrd-nav-title">{d.label}</span>
                  </span>
                </BSNav.Link>
              );
            })}
            <InsightsNavDropdown
              variant="desktop"
              items={standaloneNavItems}
              pathname={location.pathname}
              onNavigate={(path) => navigate(path)}
            />
          </BSNav>

          {/* Right section with Ask Vantage and app drawer - desktop only */}
          <div className="navbar-right-section d-none d-lg-flex">
            {(askVantageV2Enabled || askVantageV1Enabled) && (
              <AskVantageButton
                onClick={handleAskVantageClick}
                className={location.pathname === askVantageTarget ? 'active' : ''}
                aria-label="Ask Vantage"
                $prominent={!!askVantageV2Enabled}
              >
                <img src={askVantageIcon} alt="" />
                {askVantageV2Enabled && <span>Ask Vantage</span>}
              </AskVantageButton>
            )}
            <AppDrawer />
          </div>

          {/* Hamburger menu - visible only on mobile/tablet */}
          <StyledBSNavbar.Toggle
            className="d-lg-none"
            aria-controls={`offcanvasNavbar-expand-${expand}`}
            onClick={() => setShowOffcanvas(true)}
          />

          {/* Sidebar for mobile and tablet */}
          <StyledBSNavbar.Offcanvas
            show={showOffcanvas}
            onHide={() => setShowOffcanvas(false)}
            id={`offcanvasNavbar-expand-${expand}`}
            aria-labelledby={`offcanvasNavbarLabel-expand-${expand}`}
            placement="end"
            className="d-lg-none sidebar-menu"
            backdrop={true}
            scroll={false}
            keyboard={true}
            enforceFocus={true}
            autoFocus={true}
            restoreFocus={true}
          >
            <Offcanvas.Header className="sidebar-header">
              <div className="sidebar-brand">
                <div
                  className="sidebar-logo"
                  onClick={() => { if (logoDestination) { navigate(logoDestination); setShowOffcanvas(false); } }}
                  style={{ cursor: logoDestination ? 'pointer' : 'default' }}
                  role={logoDestination ? 'button' : undefined}
                  tabIndex={logoDestination ? 0 : undefined}
                  aria-label={`Go to ${logoDestination === '/signals' ? 'Signals' : 'dashboard'}`}
                >
                  <Logo />
                </div>
                <div className="sidebar-company-name">{companyName}</div>
              </div>
              <button
                type="button"
                className="custom-close-btn"
                onClick={() => setShowOffcanvas(false)}
                aria-label="Close"
              >
                <IoClose size={24} />
              </button>
            </Offcanvas.Header>
            <Offcanvas.Body className="sidebar-body">
              <div className="navigation-section">
                <BSNav
                  className="sidebar-nav flex-column"
                  role="navigation"
                  aria-label="Main navigation menu"
                >
                  {visibleSignalsStatus && (
                    <BSNav.Link
                      as="div"
                      className={`nav-item ${location.pathname === '/signals' ? 'active' : ''}`}
                    >
                      <SignalsNavItem
                        variant="mobile"
                        status={visibleSignalsStatus}
                        isSelected={location.pathname === '/signals'}
                        onClick={() => {
                          navigate('/signals');
                          setShowOffcanvas(false);
                        }}
                      />
                    </BSNav.Link>
                  )}
                  {accessibleDashboards.map((d: Dashboard, i: number) => {
                    const isSelected = d.api_path === path;
                    return (
                      <BSNav.Link
                        key={d.api_path}
                        className={`nav-item ${isSelected ? 'active' : ''}`}
                        onClick={() => handleNavigation(`/${d.api_path}`)}
                        onKeyDown={(e) => handleKeyDown(e, `/${d.api_path}`)}
                        tabIndex={0}
                        role="button"
                        aria-current={isSelected ? 'page' : undefined}
                        aria-label={`Navigate to ${d.label}`}
                      >
                        <div className="nav-item-container">
                          <div className="nav-item-content">
                            <div className="nav-item-icon">
                              <NavItemIcon name={d.identifier} isSelected={isSelected} />
                            </div>
                            <div className="nav-item-text">
                              <div className="nav-item-title">{d.label}</div>
                            </div>
                          </div>
                          {isSelected && <div className="nav-item-indicator" />}
                        </div>
                      </BSNav.Link>
                    );
                  })}
                  {standaloneNavItems.length > 0 && (
                    <BSNav.Link as="div" className="nav-item">
                      <InsightsNavDropdown
                        variant="mobile"
                        items={standaloneNavItems}
                        pathname={location.pathname}
                        onNavigate={(path) => {
                          navigate(path);
                          setShowOffcanvas(false);
                        }}
                      />
                    </BSNav.Link>
                  )}
                </BSNav>
              </div>
            </Offcanvas.Body>
            <div className="sidebar-footer">
              <div className="footer-content">
                {(askVantageV2Enabled || askVantageV1Enabled) && (
                  <MobileAskVantageButton
                    onClick={() => {
                      handleAskVantageClick();
                      setShowOffcanvas(false);
                    }}
                    className={location.pathname === askVantageTarget ? 'active' : ''}
                    aria-label="Ask Vantage"
                    $prominent={!!askVantageV2Enabled}
                  >
                    <img src={askVantageIcon} alt="" />
                    {askVantageV2Enabled && <span>Ask Vantage</span>}
                  </MobileAskVantageButton>
                )}
                <div className="theme-section">
                  <span className="theme-label">Theme</span>
                  <ThemeToggle />
                </div>
              </div>
            </div>
          </StyledBSNavbar.Offcanvas>
        </div>
      </StyledBSNavbar>
    </>
  );
};

export default Navbar;

const StyledBSNavbar = styled(BSNavbar)<{ fixed: 'top' | null; top: number }>`
  ${({ fixed, top }) =>
    fixed === 'top' &&
    `
    position: fixed;
    top: ${top}px;
  `}
  z-index: 500;
  width: 100%;
  height: 60px;
  padding: 0px;

  .container-fluid {
    padding: 0;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .navbar-left-section {
    display: flex;
    height: 100%;

    @media (max-width: 991px) {
      width: 100%;
      justify-content: center;
    }
  }

  .navbar-right-section {
    margin-right: 15px;
    align-items: center;
    display: flex;
    gap: 8px;
  }

  .company-logo {
    background-color: rgb(32, 32, 36);
    border-right: 1px solid #494c52;
    height: 100%;
    padding: 0px 5px;
    display: flex;
    align-items: center;
  }

  .navbar-brand {
    display: flex;
    height: 100%;
    align-items: center;
    padding: 0px;

    @media (max-width: 991px) {
      justify-content: center;
      position: relative;
      width: 100%;
    }

    .company-name-wrapper {
      border-right: 1px solid #494c52;
      align-content: center;
      padding: 0 15px;
      font-size: 18px;
      font-weight: 700;
      width: auto;
      white-space: nowrap;
      height: 100%;
      display: flex;
      align-items: center;

      @media (max-width: 991px) {
        border-right: none;
      }

      &.enable-selection {
        cursor: pointer;
      }

      > span {
        .company-name {
          display: inline-block;
          overflow: hidden;
          white-space: nowrap;
          max-width: 150px;
          text-overflow: ellipsis;

          @media (min-width: 992px) {
            max-width: 200px;
          }
        }

        > svg {
          position: relative;
          top: -2px;
        }
      }
    }
  }

  .main-nav {
    height: 100%;

    .nav-link {
      align-content: center;
      text-align: center;
      border-right: 1px solid rgb(73, 76, 82);
      height: 100%;
      display: flex;
      align-items: center;

      .dbrd-nav-item {
        font-size: 16px;
        padding: 0 5px;
        display: flex;
        align-items: center;

        &:last-child {
          border-right: none;
        }

        &.selected {
          color: #0d8a6a;
        }

        .dbrd-nav-title {
          margin-left: 10px;
        }
      }
    }
  }

  .navbar-toggler {
    margin-right: 15px;
    z-index: 2;
    position: absolute;
    right: 0;
  }

  /* Responsive improvements */
  @media (max-width: 576px) {
    .company-name-wrapper {
      padding: 0 8px !important;
      font-size: 16px !important;

      > span .company-name {
        max-width: 100px;
      }
    }
  }
`;

// Ask Vantage button — brand spec from design: purple→teal(70% @ 50%)→blue fill,
// solid indigo border. Teal overlay is composited over the base gradient (Figma stops).
const ASK_BTN_FILL_BASE = 'linear-gradient(90deg, #81239E 0%, #4C50AF 100%)';
const ASK_BTN_FILL_OVERLAY =
  'linear-gradient(90deg, rgba(12, 201, 191, 0) 0%, rgba(12, 201, 191, 0.7) 50%, rgba(12, 201, 191, 0) 100%)';
const ASK_BTN_FILL = `${ASK_BTN_FILL_OVERLAY}, ${ASK_BTN_FILL_BASE}`;
const ASK_BTN_BORDER_COLOR = '#312D8D';

const AskVantageButton = styled.button<{ $prominent?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $prominent }) => ($prominent ? '7px' : '0')};
  background: ${({ $prominent }) => ($prominent ? ASK_BTN_FILL : 'transparent')};
  border: ${({ $prominent }) => ($prominent ? `1.5px solid ${ASK_BTN_BORDER_COLOR}` : 'none')};
  padding: ${({ $prominent }) => ($prominent ? '7px 16px' : '8px')};
  border-radius: ${({ $prominent }) => ($prominent ? '100px' : '8px')};
  cursor: pointer;
  transition: filter 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  text-decoration: none;
  box-shadow: ${({ $prominent }) =>
    $prominent ? '0 2px 10px rgba(76, 80, 175, 0.35)' : 'none'};

  span {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
  }

  &:hover {
    background: ${({ $prominent }) => ($prominent ? ASK_BTN_FILL : 'rgba(255, 255, 255, 0.1)')};
    box-shadow: ${({ $prominent }) =>
      $prominent ? '0 4px 16px rgba(76, 80, 175, 0.5)' : 'none'};
    filter: ${({ $prominent }) => ($prominent ? 'brightness(1.08)' : 'none')};
  }

  &.active {
    background: ${({ $prominent }) =>
      $prominent ? ASK_BTN_FILL : 'rgba(13, 138, 106, 0.2)'};
    box-shadow: ${({ $prominent }) =>
      $prominent
        ? '0 0 0 2px rgba(28, 136, 159, 0.5), 0 2px 10px rgba(76, 80, 175, 0.35)'
        : 'none'};
  }

  img {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const MobileAskVantageButton = styled(AskVantageButton)`
  width: 100%;
  margin-bottom: 16px;
  min-height: 44px;
`;
